/*
* Client contacts search view for authenticated users
*/

import '@progress/kendo-ui';
import * as React from 'react';
import * as _ from 'lodash';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import VirtualizedSelect from 'react-virtualized-select';
import { Grid } from '@progress/kendo-grid-react-wrapper';
import { Row } from 'react-bootstrap';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { connectRequest } from 'redux-query';
import { withRouter } from 'react-router-dom';

import * as Actions from '../actions';
import * as AppComp from '../common/app-component-base';
import * as AppUtilities from '../common/apputilities';
import * as ReduxQueries from '../queries';
import ApiEndpoints from '../config/apiendpoints';
import AppConfiguration from '../config/configuration';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { FormUtilities } from '../common/formutilities';
import { Formatters } from '../common/formatters';
import { GridUtilities } from '../common/grid/gridutilities';
import { NavigationLinks } from '../common/navigation';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';

export interface IClientContactsSearch extends AppComp.IAppComponentProps {
    filterByRegions: any;
    filterByClientTypes: any;
}

class ClientContactsSearch extends AppComp.AppComponent<IClientContactsSearch, any> {
    public MomentDateFormat: string = AppConfiguration.getMomentShortDateFormat();

    public gridOptions = GridUtilities.kendoGridOptionsPaging(
        [
            {
                title: this.appTextGridColumnHeader('client.client-details'),
                columns: [
                    GridUtilities.kendoGridColumnDefinition('clientId', this.appTextGridColumnHeader('client.client-id'), 40, 200),
                    GridUtilities.kendoGridColumnDefinition('clientName', this.appTextGridColumnHeader('client.client-name'), 200, 220),
                    GridUtilities.kendoGridColumnDefinition('clientPhone', this.appTextGridColumnHeader('client.client-phone'), 75, 240),
                    GridUtilities.kendoGridColumnDefinition('clientTypeName', this.appTextGridColumnHeader('client.client-type-name'), 60, 380),
                    GridUtilities.kendoGridColumnDefinition('clientRegionName', this.appTextGridColumnHeader('client.client-region-name'), 60, 400),
                ]
            },
            {
                title: this.appTextGridColumnHeader('contact.client-contact-details'),
                columns: [
                    GridUtilities.kendoGridColumnDefinition('contactId', this.appTextGridColumnHeader('contact.contact-id'), 40, 245),
                    GridUtilities.kendoGridColumnDefinition('contactName', this.appTextGridColumnHeader('contact.contact-name'), 110, 260),
                    GridUtilities.kendoGridColumnDefinition('contactTitle', this.appTextGridColumnHeader('contact.contact-title'), 140, 320),
                    GridUtilities.kendoGridColumnDefinition('contactPhone', this.appTextGridColumnHeader('contact.contact-phone'), 75, 340),
                    GridUtilities.kendoGridColumnDefinition('contactEmail', this.appTextGridColumnHeader('contact.contact-email'), 140, 360),
                ]
            },
            GridUtilities.kendoGridColumnDefinition('clientAddress1', this.appTextGridColumnHeader('client.client-address1'), 1, 440, undefined, {}, undefined, 'grid-col-hidden-export-only'),
            GridUtilities.kendoGridColumnDefinition('clientAddress2', this.appTextGridColumnHeader('client.client-address2'), 1, 460, undefined, {}, undefined, 'grid-col-hidden-export-only'),
            GridUtilities.kendoGridColumnDefinition('clientCity', this.appTextGridColumnHeader('client.client-city'), 1, 480, undefined, {}, undefined, 'grid-col-hidden-export-only'),
            GridUtilities.kendoGridColumnDefinition('clientState', this.appTextGridColumnHeader('client.client-state'), 1, 500, undefined, {}, undefined, 'grid-col-hidden-export-only'),
            GridUtilities.kendoGridColumnDefinition('clientZip', this.appTextGridColumnHeader('client.client-zip'), 1, 520, undefined, {}, undefined, 'grid-col-hidden-export-only'),
            GridUtilities.kendoGridColumnDefinition('clientCityStateZip', this.appTextGridColumnHeader('client.client-city-state-zip'), 1, 540, undefined, {}, undefined, 'grid-col-hidden-export-only'),
            GridUtilities.kendoGridColumnDefinition('contactSalutation', this.appTextGridColumnHeader('contact.contact-salutation'), 1, 560, undefined, {}, undefined, 'grid-col-hidden-export-only'),
            GridUtilities.kendoGridColumnDefinition('contactFirstName', this.appTextGridColumnHeader('contact.contact-first-name'), 1, 560, undefined, {}, undefined, 'grid-col-hidden-export-only'),
            GridUtilities.kendoGridColumnDefinition('contactLastName', this.appTextGridColumnHeader('contact.contact-last-name'), 1, 560, undefined, {}, undefined, 'grid-col-hidden-export-only'),
            GridUtilities.kendoGridColumnDefinition('contactNote', this.appTextGridColumnHeader('contact.contact-note'), 1, 420, undefined, {}, undefined, 'grid-col-hidden-export-only'),
        ],
        this.appText('no-results'), { groupable: false }, this.appTextPageTitle()
    );

    public constructor(props: IClientContactsSearch) {
        super(props, 'app.client-contacts-search.');
    }

    public extendGridData(records: Array<any>) {
        // Extend grid row data
        if (!records) { return []; }

        return _.flattenDeep(records.map( r => {
            let record = _.cloneDeep(r);

            record.clientName = Formatters.getStr(record, 'name');

            const rLocation = _.get(record, 'location', {});
            record.clientAddress1 = Formatters.getStr(rLocation, 'address1');
            record.clientAddress2 = Formatters.getStr(rLocation, 'address2');
            record.clientCity = Formatters.getStr(rLocation, 'city');
            record.clientState = Formatters.getStr(rLocation, 'stateCode');
            record.clientZip = Formatters.getStr(rLocation, 'postalCode');
            record.clientCityStateZip = Formatters.composeLocationCityStateZipCountry(rLocation);

            record.clientPhone = Formatters.formatPhoneNumberNational(Formatters.getStr(record, 'phone'));
            record.clientRegionName = Formatters.getStr(record, 'region.name');
            record.clientTypeName = Formatters.getStr(record, 'clientType.name');

            const rContacts = _.get(record, 'contacts', []);

            let results = _.stubArray();

            if (_.size(rContacts) >= 1) {
                results = _.map(_.orderBy(rContacts, ['contactId'], ['desc']), rContact => {
                    let rcRes = _.cloneDeep(record);

                    rcRes.contactId = Formatters.getInt(rContact, 'contactId');
                    rcRes.contactName = Formatters.composeClientContactLetter(rContact, true);
                    rcRes.contactSalutation = Formatters.getStr(rContact, 'salutation');
                    rcRes.contactFirstName = Formatters.getStr(rContact, 'firstName');
                    rcRes.contactLastName = Formatters.getStr(rContact, 'lastName');
                    rcRes.contactTitle = Formatters.getStr(rContact, 'title');
                    rcRes.contactPhone = Formatters.formatPhoneNumberNational(Formatters.getStr(rContact, 'phone'));
                    rcRes.contactEmail = Formatters.getStr(rContact, 'email');
                    const notesAll = _.get(rContact, 'notes', []);
                    rcRes.contactNotesAll = notesAll;
                    rcRes.contactNote = Formatters.getStr(notesAll, '[0].notes');
                    rcRes.contacts = undefined;
                    rcRes.notes = undefined;

                    return rcRes;
                });
            } else {
                results.push(record);
            }

            return results;
        }));
    }

    public handleSelectRegion(e: any) {
        const value = e ? e.value : '';
        this.props.dispatch(Actions.searchClientContactsFilterRegionId(value));
    }

    public handleSelectClientType(e: any) {
        const value = e ? e.value : '';
        this.props.dispatch(Actions.searchClientContactsFilterClientTypeId(value));
    }

    public render() {
        const { search, filterByRegions, filterByClientTypes, ...rest } = this.props;
        const rows = this.extendGridData(search.results);

        const dataSource = GridUtilities.kendoGridDataSourcePaged(rows, {});

        const filterOptionAny = this.appTextFilterOptionAny();
        const regions = FormUtilities.getExtendedFilterOptions(filterByRegions, 'regionId', 'label', filterOptionAny, -1);
        const clientTypes = FormUtilities.getExtendedFilterOptions(filterByClientTypes, 'clientTypeId', 'label', filterOptionAny, -1);

        // Search filters
        const searchFilters = search.filters;

        let page = (<div />);
        const pageTitle = this.appTextResultsAndPageTitle(rows.length);

        if (_.size(rows)) {
            // console.log(`render rows extendGridData`, { rows });

            page = (
                <WhiteBoxPageSection title={pageTitle}>
                    <Row>
                        <Col xs={12}>
                            <form onSubmit={e => { e.preventDefault(); }}>
                                <Row className="form-group">
                                    <Col xl={3} xs={4}>
                                        <label className="control-label">{this.appText('filter-by-region')}</label>
                                        <VirtualizedSelect
                                            clearable={false}
                                            filterOptions={regions.filter}
                                            name="region-filter"
                                            onChange={e => this.handleSelectRegion(e)}
                                            options={regions.options}
                                            value={searchFilters.regionId}
                                        />
                                    </Col>

                                    <Col xl={3} xs={4}>
                                        <label className="control-label">{this.appText('filter-by-client-type')}</label>
                                        <VirtualizedSelect
                                            clearable={false}
                                            filterOptions={clientTypes.filter}
                                            name="client-type-filter"
                                            onChange={e => this.handleSelectClientType(e)}
                                            options={clientTypes.options}
                                            value={searchFilters.clientTypeId}
                                        />
                                    </Col>
                                </Row>
                            </form>
                        </Col>
                    </Row>

                    <Row>
                        <Col xs={12}>
                            <Grid dataSource={dataSource} {...this.gridOptions} />
                        </Col>
                    </Row>
                </WhiteBoxPageSection>
            );
        }

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrl = () => ApiEndpoints.getSearchClientContactsUrl();

const mapPropsToConfigs = (props) => ReduxQueries.Queries.searchClientContactsRequest(
    getApiUrl(),
    ReduxQueries.PropsAccessToken(props),
    _.get(props, 'search.filters.regionId', -1),
    _.get(props, 'search.filters.clientTypeId', -1),
);

const mapStateToProps = (state, props) => {
    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(), state, props, {
        filterByRegions: state.entities.searchClientContactsRegions,
        filterByClientTypes: state.entities.searchClientContactsClientTypes,
        search: {
            results: state.entities.searchClientContactsResults,
            filters: {
                regionId: state.searchFiltersClientContacts.regionId,
                clientTypeId: state.searchFiltersClientContacts.clientTypeId,
            }
        },
    });
};

const ClientContactsSearchContainer = compose<any>(
    connect(mapStateToProps),
    connectRequest(mapPropsToConfigs)
  )(ClientContactsSearch);

export default withRouter(ClientContactsSearchContainer);



// WEBPACK FOOTER //
// ./src/client-search/client-contacts-search.tsx